import React, { useEffect, useState } from "react";
import TournamentWizard from "./TournamentWizard";
import TournamentResults from "./TournamentResults";
import {
  Tournament,
  TournamentDisplayStatus,
  canScore,
  resyncHandicaps,
  summarize,
  tournamentDisplayStatus,
} from "./tournament";
import { loadOwner, loadRoster } from "./roster";
import {
  GroupScores,
  fetchTournamentScores,
  todayLocalISO,
} from "./tournamentPlay";
import {
  deleteTournament as deleteTournamentApi,
  listTournaments,
  saveTournament,
} from "./tournamentsApi";
import "./caddiegames.css";

// ---------------------------------------------------------------------------
// CaddieGames — Admin dashboard
//
// Surfaced under the gearwheel as "Manage CaddieGames" (route /manage-caddiegames)
// for users holding the caddieGames.admin permission. The admin's home for
// building tournaments: pick a course, assign players into groups, choose the
// games — players then receive an invite and drop straight into a pre-configured
// round instead of setting it up themselves.
//
// Tournaments persist to the backend (CaddieGamesTournaments); on the day,
// connected group members see them on their course-select screen and tap in.
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<TournamentDisplayStatus, string> = {
  draft: "Draft",
  pending: "Pending",
  open: "Open",
  ended: "Ended",
};

function formatDate(iso: string): string {
  // Parse as a plain calendar date (no time-zone shift) and render short.
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso || "No date set";
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type View = "dashboard" | "wizard" | "detail";

export default function CaddieGamesAdmin() {
  const [view, setView] = useState<View>("dashboard");
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  // Tournament being edited in the wizard (null = creating a new one).
  const [editing, setEditing] = useState<Tournament | null>(null);
  // Tournament open in the detail view.
  const [detailId, setDetailId] = useState<string | null>(null);
  // Whether the read-only View Scores results overlay is open (detail view),
  // plus the scores pre-fetched behind the button spinner so the modal opens
  // fully loaded.
  const [showScores, setShowScores] = useState(false);
  const [scoresGroups, setScoresGroups] = useState<GroupScores[] | null>(null);
  // Set when a tournament save fails — surfaced in the wizard so a failed
  // create/edit is never mistaken for a successful one.
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load the admin's tournaments on mount.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const list = await listTournaments();
      if (alive) {
        setTournaments(list);
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const detailTournament = tournaments.find((t) => t.id === detailId) || null;

  const openCreate = () => {
    setEditing(null);
    setDetailId(null);
    setSaveError(null);
    setView("wizard");
  };

  const openEdit = (t: Tournament) => {
    setEditing(t);
    setSaveError(null);
    setView("wizard");
  };

  const openDetail = (id: string) => {
    setDetailId(id);
    setShowScores(false);
    setScoresGroups(null);
    setView("detail");
  };

  // Wizard returns a full Tournament — persist it, then replace it in place if
  // it already exists (edit) or prepend it (create).
  //
  // A failed save must NOT look like a success: we previously fell back to the
  // local copy, which listed the tournament as if it had been created and only
  // revealed the truth on reload (and it never reached players). Now the wizard
  // stays open with the draft intact and surfaces the error so it can be retried.
  const handleComplete = async (t: Tournament) => {
    const result = await saveTournament(t);
    if (!result.ok) {
      setSaveError(`Couldn't save the tournament — ${result.error}`);
      return;
    }
    const saved = result.tournament;
    setSaveError(null);
    setTournaments((prev) =>
      prev.some((x) => x.id === saved.id)
        ? prev.map((x) => (x.id === saved.id ? saved : x))
        : [saved, ...prev],
    );
    setEditing(null);
    setView("dashboard");
  };

  const deleteTournament = async (id: string) => {
    await deleteTournamentApi(id);
    setTournaments((prev) => prev.filter((t) => t.id !== id));
    setDetailId(null);
    setView("dashboard");
  };

  // Refresh a tournament's handicaps from the current roster (+ owner card) and
  // persist. The PUT response doesn't carry submittedGroupIds (derived field),
  // so we preserve the existing value locally.
  const updateHandicaps = async (t: Tournament) => {
    const resynced = resyncHandicaps(t, loadRoster(), loadOwner());
    const result = await saveTournament(resynced);
    if (!result.ok) {
      setSaveError(`Couldn't update handicaps — ${result.error}`);
      return;
    }
    const merged: Tournament = {
      ...result.tournament,
      submittedGroupIds: t.submittedGroupIds,
    };
    setSaveError(null);
    setTournaments((prev) =>
      prev.map((x) => (x.id === merged.id ? merged : x)),
    );
  };

  if (view === "wizard") {
    return (
      <TournamentWizard
        initial={editing}
        error={saveError}
        onCancel={() => {
          setEditing(null);
          setSaveError(null);
          setView(detailId ? "detail" : "dashboard");
        }}
        onComplete={handleComplete}
      />
    );
  }

  if (view === "detail" && detailTournament) {
    return (
      <>
        <TournamentDetail
          tournament={detailTournament}
          onBack={() => {
            setDetailId(null);
            setShowScores(false);
            setView("dashboard");
          }}
          onEdit={() => openEdit(detailTournament)}
          onDelete={() => deleteTournament(detailTournament.id)}
          onUpdateHandicaps={() => updateHandicaps(detailTournament)}
          onViewScores={
            detailTournament.course
              ? (groups) => {
                  setScoresGroups(groups);
                  setShowScores(true);
                }
              : undefined
          }
        />
        {showScores && detailTournament.course && (
          // Wrap in cg-page so the modal/scorecard inherit the CaddieGames
          // colour + CSS-variable scope. Without it (the admin shell renders
          // this outside any .cg-page), the leaderboard's name column inherits
          // the host app's light text colour and the names render invisibly.
          <div className="cg-page">
            <TournamentResults
              tournamentId={detailTournament.id}
              course={detailTournament.course}
              games={detailTournament.games}
              title={detailTournament.name}
              subtitle={formatDate(detailTournament.date)}
              displayTeeId={detailTournament.defaultTeeId}
              initialGroups={scoresGroups ?? undefined}
              onClose={() => setShowScores(false)}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="cg-page">
      <div className="cg-subpage cg-admin">
        <div className="cg-admin-head">
          <div>
            <h2 className="cg-admin-title">Tournaments</h2>
            <p className="cg-admin-sub">
              Create and manage CaddieGames tournaments for your players.
            </p>
          </div>
          <button
            type="button"
            className="cg-btn cg-btn-primary cg-admin-create"
            onClick={openCreate}
          >
            + Create Tournament
          </button>
        </div>

        {loading ? (
          <p className="cg-subpage-note">Loading tournaments…</p>
        ) : tournaments.length === 0 ? (
          <div className="cg-admin-empty">
            <p className="cg-admin-empty-title">No tournaments yet</p>
            <p className="cg-admin-empty-sub">
              Create your first tournament to invite players and set up their
              round.
            </p>
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={openCreate}
            >
              + Create Tournament
            </button>
          </div>
        ) : (
          <ul className="cg-tourn-list">
            {tournaments.map((t) => {
              const s = summarize(t);
              const status = tournamentDisplayStatus(t, todayLocalISO());
              return (
                <li key={t.id} className="cg-tourn-row">
                  <button
                    type="button"
                    className="cg-tourn-main"
                    onClick={() => openDetail(t.id)}
                  >
                    <div className="cg-tourn-info">
                      <div className="cg-tourn-name-line">
                        <span className="cg-tourn-name">{s.name}</span>
                        <span
                          className={`cg-tourn-pill cg-tourn-pill-${status}`}
                        >
                          {STATUS_LABEL[status]}
                        </span>
                      </div>
                      <div className="cg-tourn-meta">
                        <span>{s.courseName}</span>
                        <span>·</span>
                        <span>{formatDate(s.date)}</span>
                      </div>
                    </div>
                    <span className="cg-tourn-chevron">›</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── Detail view ─────────────────────────

function TournamentDetail({
  tournament,
  onBack,
  onEdit,
  onDelete,
  onUpdateHandicaps,
  onViewScores,
}: {
  tournament: Tournament;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpdateHandicaps: () => void | Promise<void>;
  // Open the read-only results (leaderboard + combined card) with scores the
  // button pre-fetched. Undefined when the tournament has no course yet.
  onViewScores?: (groups: GroupScores[]) => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resynced, setResynced] = useState(false);
  const [loadingScores, setLoadingScores] = useState(false);
  const s = summarize(tournament);
  const status = tournamentDisplayStatus(tournament, todayLocalISO());
  const ended = status === "ended";
  const submitted = new Set(tournament.submittedGroupIds ?? []);

  const handleUpdateHandicaps = async () => {
    setResyncing(true);
    setResynced(false);
    await onUpdateHandicaps();
    setResyncing(false);
    setResynced(true);
  };

  // Load the cross-group scores first (spinner on the button), then open the
  // results — so the leaderboard appears fully loaded instead of a small modal
  // that expands as it fetches.
  const handleViewScores = async () => {
    if (!onViewScores || loadingScores) return;
    setLoadingScores(true);
    try {
      const groups = await fetchTournamentScores(tournament.id);
      onViewScores(groups);
    } finally {
      setLoadingScores(false);
    }
  };

  return (
    <div className="cg-page">
      <div className="cg-subpage cg-admin">
        <div className="cg-subpage-head">
          <button type="button" className="cg-btn cg-btn-ghost" onClick={onBack}>
            ← Back
          </button>
          <h2>{tournament.name}</h2>
          <span className={`cg-tourn-pill cg-tourn-pill-${status}`}>
            {STATUS_LABEL[status]}
          </span>
        </div>

        <div className="cg-review-block">
          <div className="cg-review-label">Course</div>
          <div className="cg-review-value">{s.courseName}</div>
          <div className="cg-review-sub">{formatDate(s.date)}</div>
        </div>

        <div className="cg-review-block">
          <div className="cg-review-label">Groups ({s.groupCount})</div>
          {tournament.groups.map((g) => (
            <div key={g.id} className="cg-review-group">
              <div className="cg-review-group-name">
                {submitted.has(g.id) && (
                  <span
                    className="cg-review-group-check"
                    role="img"
                    aria-label="Card submitted"
                    title="Card submitted"
                  >
                    ✓
                  </span>
                )}
                {g.name}
                {g.teeTime ? ` · ${g.teeTime}` : ""}
              </div>
              <div className="cg-review-group-players">
                {g.players.length === 0
                  ? "No players"
                  : g.players
                      .map((p) => p.name + (canScore(p) ? " ✓" : ""))
                      .join(", ")}
              </div>
            </div>
          ))}
        </div>

        <div className="cg-review-block">
          <div className="cg-review-label">Games</div>
          <div className="cg-review-value cg-review-games">
            {s.games.join(", ") || "—"}
          </div>
        </div>

        <div className="cg-detail-actions">
          <div className="cg-detail-actions-left">
            {onViewScores && (
              <button
                type="button"
                className="cg-btn cg-btn-primary cg-detail-action"
                onClick={handleViewScores}
                disabled={loadingScores}
              >
                {loadingScores ? (
                  <>
                    <span className="cg-btn-spinner" aria-hidden /> Loading…
                  </>
                ) : (
                  "View Scores"
                )}
              </button>
            )}
            <button
              type="button"
              className="cg-btn cg-detail-action"
              onClick={handleUpdateHandicaps}
              disabled={ended || resyncing}
              title={
                ended
                  ? "Tournament has ended — handicaps are locked"
                  : "Refresh handicaps from your current player list"
              }
            >
              {resyncing
                ? "Updating…"
                : resynced
                  ? "Handicaps updated ✓"
                  : "Update Handicaps"}
            </button>
          </div>
          <div className="cg-detail-actions-right">
            {confirmDelete ? (
              <div className="cg-detail-confirm">
                <span>Delete this tournament?</span>
                <button
                  type="button"
                  className="cg-btn cg-btn-danger"
                  onClick={onDelete}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost"
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="cg-btn cg-detail-edit"
                  onClick={onEdit}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost cg-detail-delete"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
